import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardTitle, Spinner } from "reactstrap";
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { apiURL } from '../../../config';
import Footer from "components/Footers/Footer";

const COLORS = ['#2dce89', '#f5365c', '#172b4d'];

const StudentDashboard = () => {
  const token = localStorage.getItem("token");

  const [studentId, setStudentId] = useState(null);
  const [basicInfo, setBasicInfo] = useState(null);
  const [className, setClassName] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const user = localStorage.getItem('userInfo');
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        setStudentId(parsedUser._id);
      } catch (error) {
        console.error("Error parsing user info from localStorage:", error);
        setError("Failed to load user info.");
        setLoading(false);
        return;
      }
    } else {
      setError("User info not found in localStorage.");
      setLoading(false);
      return;
    }
  }, []);

  useEffect(() => {
    if (!studentId) return;

    const fetchData = async () => {
      try {
        const basicInfoResponse = await axios.get(`${apiURL}/student/${studentId}/getStudentBasicInfo`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setBasicInfo(basicInfoResponse.data);

        const classNameResponse = await axios.get(`${apiURL}/student/${studentId}/getClassName`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setClassName(classNameResponse.data);

        const attendanceResponse = await axios.get(`${apiURL}/student/${studentId}/getAttendanceSummary`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setAttendance(attendanceResponse.data);

        const subjectsResponse = await axios.get(`${apiURL}/student/${studentId}/getCourseSubjectsAndTeachers`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        setSubjects(subjectsResponse.data);

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [studentId, token]);

  if (loading) {
    return <p>Loading student data...</p>;
  }

  if (error) {
    return <p>NO RECORD FOUND</p>;
  }

  const attendanceData = attendance ? [
    { name: 'Present', value: attendance.presentPercentage, color: COLORS[0] },
    { name: 'Absent', value: attendance.absentPercentage, color: COLORS[1] },
    { name: 'Leave', value: attendance.leavePercentage, color: COLORS[2] },
  ] : [];

  return (
    <Container>
      <Row>
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Personal Information</CardTitle>
              {basicInfo ? (
                <>
                  <p><strong>Registration Number:</strong> {basicInfo.registrationNumber}</p>
                  <p><strong>Full Name:</strong> {basicInfo.fullName}</p>
                  <p><strong>Class:</strong> {basicInfo.class}</p>
                  <p><strong>Father/Guardian Details:</strong></p>
                  <p>&nbsp;&nbsp;<strong>Name:</strong> {basicInfo.fatherGuardianDetails?.name || 'N/A'}</p>
                  <p>&nbsp;&nbsp;<strong>Profession:</strong> {basicInfo.fatherGuardianDetails?.profession || 'N/A'}</p>
                  <p>&nbsp;&nbsp;<strong>Address:</strong> {basicInfo.fatherGuardianDetails?.address || 'N/A'}</p>
                  <p>&nbsp;&nbsp;<strong>Relationship:</strong> {basicInfo.fatherGuardianDetails?.relationship || 'N/A'}</p>
                  <p>&nbsp;&nbsp;<strong>Phone Number:</strong> {basicInfo.fatherGuardianDetails?.phoneNumber || 'N/A'}</p>
                </>
              ) : (
                <p>No basic information available.</p>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Class Information</CardTitle>
              {className ? (
                <p><strong>Class Name:</strong> {className.className}</p>
              ) : (
                <p>No class information available.</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Attendance Summary</CardTitle>
              {attendanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={attendanceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {attendanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p>No attendance data available.</p>
              )}
              {attendance && (
                <>
                  <p><strong>Present:</strong> {attendance.presentPercentage?.toFixed(2) || 0}%</p>
                  <p><strong>Absent:</strong> {attendance.absentPercentage?.toFixed(2) || 0}%</p>
                  <p><strong>Leave:</strong> {attendance.leavePercentage?.toFixed(2) || 0}%</p>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Subjects and Staffs</CardTitle>
              {subjects ? (
                <>
                  <p><strong>Student Name:</strong> {subjects.studentName}</p>
                  <p><strong>Registration Number:</strong> {subjects.studentRegistrationNumber}</p>
                  <p><strong>Course Name:</strong> {subjects.courseName}</p>
                  <p><strong>Course Code:</strong> {subjects.courseCode}</p>
                  {subjects.subjectsAndTeachers && subjects.subjectsAndTeachers.length > 0 ? (
                    <ul>
                      {subjects.subjectsAndTeachers.map((item, index) => (
                        <li key={index}>
                          <strong>Subject:</strong> {item?.subjectName || 'N/A'} ({item?.subjectCode || 'N/A'}), <strong>Staff:</strong> {item?.teacherName || 'N/A'}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No subjects and staffs available.</p>
                  )}
                </>
              ) : (
                <p>No subjects data available.</p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default StudentDashboard;
